import React, { useState, useEffect } from 'react';
import { Calculator, Save, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

interface Employee {
  id: string;
  first_name: string;  // Zmienione z firstName
  last_name: string;   // Zmienione z lastName
  position: string;
  department: string;
  employment_type: 'umowa o pracę' | 'umowa zlecenie' | 'umowa o dzieło' | 'B2B';  // Zmienione z employmentType
  base_salary: number; // Zmienione z baseSalary
  hourly_rate: number; // Zmienione z hourlyRate
}

interface PayrollCalculation {
  employee_id: string;
  month: string;
  regular_hours: number;
  overtime_50: number;
  overtime_100: number;
  overtime_regular: number;
  night_hours: number;
  weekend_hours: number;
  holiday_hours: number;
  base_salary: number;
  overtime_pay_50: number;
  overtime_pay_100: number;
  overtime_pay_regular: number;
  night_pay: number;
  weekend_pay: number;
  holiday_pay: number;
  gross_salary: number;
  pension_contribution: number;
  disability_contribution: number;
  sickness_contribution: number;
  health_contribution: number;
  income_tax: number;
  net_salary: number;
}

interface PayrollCalculatorProps {
  onSaveSuccess?: () => void;
}

const PayrollCalculator: React.FC<PayrollCalculatorProps> = ({ onSaveSuccess }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [month, setMonth] = useState<string>(new Date().toISOString().slice(0, 7));
  const [hours, setHours] = useState({
    regular: 168,
    overtime50: 0,
    overtime100: 0,
    overtimeRegular: 0,
    night: 0,
    weekend: 0,
    holiday: 0
  });
  const [calculation, setCalculation] = useState<PayrollCalculation | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState('');

  // Załaduj pracowników z Supabase
  useEffect(() => {
    loadEmployees();
  }, []);

  // Załaduj zapisane dane rozliczenia z Supabase
  useEffect(() => {
    if (selectedEmployee && month) {
      loadPayrollData(selectedEmployee, month);
    }
  }, [selectedEmployee, month]);

  const loadEmployees = async () => {
    try {
      setIsLoading(true);
      console.log('Loading employees from Supabase...');
      
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .order('last_name', { ascending: true });

      console.log('Supabase response:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (data && data.length > 0) {
        console.log('Loaded employees:', data);
        setEmployees(data);
      } else {
        console.log('No employees found, creating sample ones...');
        await createSampleEmployees();
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setSaveMessage('Błąd podczas ładowania pracowników: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const createSampleEmployees = async () => {
    const sampleEmployees = [
      {
        first_name: 'Jan',
        last_name: 'Kowalski',
        position: 'Operator CNC',
        department: 'Produkcja',
        employment_type: 'umowa o pracę' as const,
        base_salary: 4500,
        hourly_rate: 25.89
      },
      {
        first_name: 'Anna',
        last_name: 'Nowak',
        position: 'Technolog',
        department: 'Biuro',
        employment_type: 'umowa o pracę' as const,
        base_salary: 6000,
        hourly_rate: 34.52
      }
    ];

    try {
      const { data, error } = await supabase
        .from('employees')
        .insert(sampleEmployees)
        .select();

      if (error) throw error;
      if (data) setEmployees(data);
    } catch (error) {
      console.error('Error creating sample employees:', error);
    }
  };

  const loadPayrollData = async (employeeId: string, month: string) => {
    try {
      const { data, error } = await supabase
        .from('payroll_calculations')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('month', month)
        .maybeSingle(); // Użyj maybeSingle zamiast single

      if (error) {
        // Sprawdź różne typy błędów
        if (error.code === 'PGRST116' || error.code === '406') {
          console.log('No existing payroll data for this month');
          return;
        } else if (error.message && error.message.includes('relation') && error.message.includes('does not exist')) {
          console.error('Table payroll_calculations does not exist');
          setSaveMessage('Błąd: Tabela rozliczeń nie istnieje. Wykonaj skrypt payroll_calculations_table.sql w Supabase.');
          return;
        } else if (error.code === '42501') {
          console.error('Permission denied for table payroll_calculations');
          setSaveMessage('Błąd: Brak uprawnień do tabeli. Wykonaj skrypt fix_payroll_406_error.sql w Supabase.');
          return;
        }
        console.error('Error loading payroll data:', error);
        // Nie rzucaj błędu dla 406 - po prostu kontynuuj
        if (error.message && error.message.includes('406')) {
          return;
        }
        throw error;
      }

      if (data) {
        setHours({
          regular: data.regular_hours,
          overtime50: data.overtime_50,
          overtime100: data.overtime_100,
          overtimeRegular: data.overtime_regular,
          night: data.night_hours,
          weekend: data.weekend_hours,
          holiday: data.holiday_hours
        });
      }
    } catch (error) {
      console.error('Error loading payroll data:', error);
    }
  };

  const calculatePayroll = () => {
    const employee = employees.find(e => e.id === selectedEmployee);
    if (!employee) return;

    console.log('Calculating payroll for employee:', employee);
    console.log('Hours:', hours);

    const hourlyRate = employee.hourly_rate || 0;
    
    const basePay = hours.regular * hourlyRate;
    const overtime50Pay = hours.overtime50 * hourlyRate * 1.5;
    const overtime100Pay = hours.overtime100 * hourlyRate * 2.0;
    const overtimeRegularPay = hours.overtimeRegular * hourlyRate;
    const nightPay = hours.night * hourlyRate * 0.2;
    const weekendPay = hours.weekend * hourlyRate * 0.2;
    const holidayPay = hours.holiday * hourlyRate * 1.0;
    
    const grossSalary = basePay + overtime50Pay + overtime100Pay + overtimeRegularPay + 
                       nightPay + weekendPay + holidayPay;
    
    console.log('Calculated amounts:', {
      basePay,
      overtime50Pay,
      overtime100Pay,
      grossSalary
    });
    
    let pensionContribution = 0;
    let disabilityContribution = 0;
    let sicknessContribution = 0;
    let healthContribution = 0;
    let incomeTax = 0;
    
    if (employee.employment_type === 'umowa o pracę' || employee.contract_type === 'employment') {
      pensionContribution = grossSalary * 0.0976;
      disabilityContribution = grossSalary * 0.015;
      sicknessContribution = grossSalary * 0.0245;
      
      const socialBase = grossSalary - pensionContribution - disabilityContribution - sicknessContribution;
      healthContribution = socialBase * 0.09;
      
      const taxBase = socialBase - 250;
      incomeTax = Math.max(0, taxBase * 0.12);
    }
    
    const totalDeductions = pensionContribution + disabilityContribution + 
                          sicknessContribution + healthContribution + incomeTax;
    const netSalary = grossSalary - totalDeductions;
    
    const calc: PayrollCalculation = {
      employee_id: selectedEmployee,
      month: month,
      regular_hours: hours.regular,
      overtime_50: hours.overtime50,
      overtime_100: hours.overtime100,
      overtime_regular: hours.overtimeRegular,
      night_hours: hours.night,
      weekend_hours: hours.weekend,
      holiday_hours: hours.holiday,
      base_salary: basePay,
      overtime_pay_50: overtime50Pay,
      overtime_pay_100: overtime100Pay,
      overtime_pay_regular: overtimeRegularPay,
      night_pay: nightPay,
      weekend_pay: weekendPay,
      holiday_pay: holidayPay,
      gross_salary: grossSalary,
      pension_contribution: pensionContribution,
      disability_contribution: disabilityContribution,
      sickness_contribution: sicknessContribution,
      health_contribution: healthContribution,
      income_tax: incomeTax,
      net_salary: netSalary
    };
    
    setCalculation(calc);
  };

  const saveCalculation = async () => {
    if (!calculation) {
      console.error('No calculation to save');
      return;
    }
    
    console.log('Saving calculation:', calculation);
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Sprawdź czy rozliczenie już istnieje
      console.log('Checking for existing calculation...');
      const { data: existing, error: checkError } = await supabase
        .from('payroll_calculations')
        .select('id')
        .eq('employee_id', calculation.employee_id)
        .eq('month', calculation.month)
        .maybeSingle(); // Użyj maybeSingle
        
      console.log('Check result:', { existing, checkError });
      
      // Ignoruj błąd 406 przy sprawdzaniu
      if (checkError && checkError.code !== '406' && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      let result;
      if (existing && !checkError) {
        console.log('Updating existing calculation with id:', existing.id);
        result = await supabase
          .from('payroll_calculations')
          .update(calculation)
          .eq('id', existing.id)
          .select();
      } else {
        console.log('Creating new calculation');
        result = await supabase
          .from('payroll_calculations')
          .insert(calculation)
          .select();
      }

      console.log('Save result:', result);
      
      if (result.error) {
        console.error('Save error details:', result.error);
        
        // Specjalna obsługa błędu 406
        if (result.error.code === '406' || (result.error.message && result.error.message.includes('406'))) {
          throw new Error('Błąd dostępu do tabeli (406). Wykonaj skrypt fix_payroll_406_error.sql w Supabase SQL Editor.');
        }
        
        throw result.error;
      }

      setSaveMessage('Rozliczenie zostało zapisane pomyślnie!');
      
      // Wywołaj callback jeśli jest
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
      setTimeout(() => {
        setSaveMessage('');
      }, 3000);
    } catch (error) {
      console.error('Save error:', error);
      if (error.message && error.message.includes('relation') && error.message.includes('does not exist')) {
        setSaveMessage('Błąd: Tabela rozliczeń nie istnieje. Wykonaj skrypt payroll_calculations_table.sql w Supabase.');
      } else {
        if (error.message && error.message.includes('406')) {
        setSaveMessage('Błąd 406: Problem z dostępem do tabeli. Wykonaj w Supabase SQL Editor: \n' +
                      '1. ALTER TABLE payroll_calculations DISABLE ROW LEVEL SECURITY;\n' +
                      '2. GRANT ALL ON payroll_calculations TO anon;\n' +
                      '3. GRANT ALL ON payroll_calculations TO authenticated;');
      } else {
        setSaveMessage('Błąd podczas zapisywania rozliczenia: ' + error.message);
      }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(value);
  };

  const selectedEmployeeData = employees.find(e => e.id === selectedEmployee);

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin mr-2 text-gray-400" />
        <span className="text-gray-300">Ładowanie danych...</span>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-semibold mb-6 flex items-center gap-2 text-white">
        <Calculator className="w-5 h-5 text-blue-500" />
        Kalkulator wynagrodzeń
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <h4 className="font-medium mb-4 text-gray-200">Dane do obliczeń</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-300">Pracownik</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-gray-700 border-gray-600 text-white"
              >
                <option value="">Wybierz pracownika</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} - {emp.position}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-300">Miesiąc rozliczeniowy</label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-gray-700 border-gray-600 text-white"
              />
            </div>

            {selectedEmployeeData && (
              <div className="text-sm text-gray-400">
                <p>Stawka godzinowa: {formatCurrency(selectedEmployeeData.hourly_rate)}</p>
                <p>Typ umowy: {selectedEmployeeData.employment_type}</p>
              </div>
            )}
          </div>
        </div>

        <div>
          <h4 className="font-medium mb-4 text-gray-200">Przepracowane godziny</h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Godziny podstawowe</label>
              <input
                type="number"
                value={hours.regular}
                onChange={(e) => setHours({...hours, regular: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Nadgodziny 50%</label>
              <input
                type="number"
                value={hours.overtime50}
                onChange={(e) => setHours({...hours, overtime50: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Nadgodziny 100%</label>
              <input
                type="number"
                value={hours.overtime100}
                onChange={(e) => setHours({...hours, overtime100: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Zwykła godzina (nadliczbowa)</label>
              <input
                type="number"
                value={hours.overtimeRegular}
                onChange={(e) => setHours({...hours, overtimeRegular: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Godziny nocne</label>
              <input
                type="number"
                value={hours.night}
                onChange={(e) => setHours({...hours, night: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Weekendy</label>
              <input
                type="number"
                value={hours.weekend}
                onChange={(e) => setHours({...hours, weekend: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-300">Święta</label>
              <input
                type="number"
                value={hours.holiday}
                onChange={(e) => setHours({...hours, holiday: Number(e.target.value)})}
                className="w-24 px-2 py-1 border rounded bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <button
          onClick={calculatePayroll}
          disabled={!selectedEmployee}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Oblicz wynagrodzenie
        </button>

        {calculation && (
          <button
            onClick={saveCalculation}
            disabled={isSaving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Zapisywanie...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Zapisz rozliczenie
              </>
            )}
          </button>
        )}
      </div>

      {saveMessage && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          saveMessage.includes('pomyślnie') 
            ? 'bg-green-900 text-green-200'
            : 'bg-red-900 text-red-200'
        }`}>
          <AlertCircle className="w-4 h-4" />
          {saveMessage}
        </div>
      )}

      {calculation && (
        <div className="border-t border-gray-700 pt-6">
          <h4 className="font-medium mb-4 text-gray-200">Wynik obliczeń</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h5 className="font-medium text-sm mb-3 text-gray-300">Podsumowanie godzin</h5>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex justify-between">
                  <span>Łącznie godzin:</span>
                  <span className="font-medium text-white">
                    {hours.regular + hours.overtime50 + hours.overtime100 + 
                     hours.overtimeRegular + hours.night + hours.weekend + hours.holiday}h
                  </span>
                </div>
                {selectedEmployeeData && (
                  <div className="flex justify-between">
                    <span>Stawka podstawowa:</span>
                    <span className="font-medium text-white">
                      {formatCurrency(selectedEmployeeData.hourly_rate)}/h
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h5 className="font-medium text-sm mb-3 text-gray-300">Wynagrodzenie</h5>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex justify-between">
                  <span>Podstawowe:</span>
                  <span>{formatCurrency(calculation.base_salary)}</span>
                </div>
                {calculation.overtime_pay_50 > 0 && (
                  <div className="flex justify-between">
                    <span>Nadgodziny 50%:</span>
                    <span>{formatCurrency(calculation.overtime_pay_50)}</span>
                  </div>
                )}
                {calculation.overtime_pay_100 > 0 && (
                  <div className="flex justify-between">
                    <span>Nadgodziny 100%:</span>
                    <span>{formatCurrency(calculation.overtime_pay_100)}</span>
                  </div>
                )}
                {calculation.overtime_pay_regular > 0 && (
                  <div className="flex justify-between">
                    <span>Zwykła godzina:</span>
                    <span>{formatCurrency(calculation.overtime_pay_regular)}</span>
                  </div>
                )}
                {calculation.night_pay > 0 && (
                  <div className="flex justify-between">
                    <span>Dodatek nocny:</span>
                    <span>{formatCurrency(calculation.night_pay)}</span>
                  </div>
                )}
                {calculation.weekend_pay > 0 && (
                  <div className="flex justify-between">
                    <span>Dodatek weekendowy:</span>
                    <span>{formatCurrency(calculation.weekend_pay)}</span>
                  </div>
                )}
                {calculation.holiday_pay > 0 && (
                  <div className="flex justify-between">
                    <span>Dodatek świąteczny:</span>
                    <span>{formatCurrency(calculation.holiday_pay)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium border-t border-gray-700 pt-2 text-white">
                  <span>Brutto:</span>
                  <span>{formatCurrency(calculation.gross_salary)}</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-medium text-sm mb-3 text-gray-300">Potrącenia</h5>
              <div className="space-y-2 text-sm text-gray-400">
                {selectedEmployeeData?.employment_type === 'umowa o pracę' && (
                  <>
                    <div className="flex justify-between">
                      <span>Składka emerytalna (9.76%):</span>
                      <span className="text-red-400">
                        -{formatCurrency(calculation.pension_contribution)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Składka rentowa (1.5%):</span>
                      <span className="text-red-400">
                        -{formatCurrency(calculation.disability_contribution)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Składka chorobowa (2.45%):</span>
                      <span className="text-red-400">
                        -{formatCurrency(calculation.sickness_contribution)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Składka zdrowotna (9%):</span>
                      <span className="text-red-400">
                        -{formatCurrency(calculation.health_contribution)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Zaliczka na podatek:</span>
                      <span className="text-red-400">
                        -{formatCurrency(calculation.income_tax)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between font-medium border-t border-gray-700 pt-2 text-green-400">
                  <span>Netto:</span>
                  <span>{formatCurrency(calculation.net_salary)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-700 rounded-lg text-sm text-gray-300">
            <p>
              <span className="font-medium text-white">Obliczenia dla:</span> {selectedEmployeeData?.first_name} {selectedEmployeeData?.last_name}
            </p>
            <p>
              <span className="font-medium text-white">Typ umowy:</span> {selectedEmployeeData?.employment_type}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollCalculator;
export { PayrollCalculator };
